/**
 * Text flow: how big a multiline text is and where its lines break — inside
 * its box, or inside a shape cut by its box (`TextElement.fitShape`).
 *
 * The rule the owner asked for (1 October 2026): wrapping is part of the
 * fit, not a last resort. A text first tries the largest size, on as many
 * lines as that size needs; only when no number of lines fits does it get
 * smaller. The renderer this replaces measured the text UNWRAPPED while
 * fitting, so a long quote shrank on one line all the way down to its
 * minimum size and only wrapped there.
 *
 * A shape changes the width each line may use: a line high in a heart is
 * wide, one near its point is narrow. Lines are centred on the box's middle,
 * a line never jumps a gap (the notch between a heart's lobes), and the
 * block is placed where the alignment asks — "center" is the shape's own
 * centre of mass, not the middle of its bounding box — and slid up or down
 * only when it would not fit there.
 *
 * Pure: the caller supplies the measurements, so the print renderer
 * (CanvasKit) and the storefront's stand-in (a 2D canvas) break lines in
 * the same places.
 */
import type { TextElement, TextFitShape } from './types.js';

// ── Paths ────────────────────────────────────────────────────────────────────

export type FlowPoint = [number, number];

const PATH_TOKEN = /[MmLlHhVvCcSsQqTtAaZz]|[-+]?(?:\d+\.?\d*|\.\d+)(?:[eE][-+]?\d+)?/g;
const isCommand = (t: string | undefined): boolean => t !== undefined && /^[A-Za-z]$/.test(t);

/**
 * An SVG path as closed polygons, every curve flattened into `segments`
 * straight pieces. Absolute and relative commands, `M L H V C S Q T A Z`.
 * Throws on a path it cannot read.
 */
export function pathPolygons(d: string, segments = 16): FlowPoint[][] {
  const tokens = d.match(PATH_TOKEN) ?? [];
  const out: FlowPoint[][] = [];
  let ring: FlowPoint[] = [];
  let i = 0;
  let cmd = '';
  let prev = '';
  let x = 0;
  let y = 0;
  let sx = 0;
  let sy = 0;
  // The last control point, which S and T reflect.
  let qx = 0;
  let qy = 0;
  const num = (): number => {
    const t = tokens[i++];
    const v = Number(t);
    if (t === undefined || isCommand(t) || !Number.isFinite(v)) throw new Error('The path could not be read.');
    return v;
  };
  const flush = (): void => {
    if (ring.length >= 3) out.push(ring);
    ring = [];
  };
  const to = (nx: number, ny: number): void => {
    if (ring.length === 0) ring.push([x, y]);
    ring.push([nx, ny]);
    x = nx;
    y = ny;
  };
  const cubic = (x1: number, y1: number, x2: number, y2: number, ex: number, ey: number): void => {
    const x0 = x;
    const y0 = y;
    for (let k = 1; k <= segments; k++) {
      const t = k / segments;
      const u = 1 - t;
      to(
        u * u * u * x0 + 3 * u * u * t * x1 + 3 * u * t * t * x2 + t * t * t * ex,
        u * u * u * y0 + 3 * u * u * t * y1 + 3 * u * t * t * y2 + t * t * t * ey,
      );
    }
  };
  const quad = (x1: number, y1: number, ex: number, ey: number): void => {
    const x0 = x;
    const y0 = y;
    for (let k = 1; k <= segments; k++) {
      const t = k / segments;
      const u = 1 - t;
      to(u * u * x0 + 2 * u * t * x1 + t * t * ex, u * u * y0 + 2 * u * t * y1 + t * t * ey);
    }
  };
  // SVG's endpoint arc, through its centre parameterisation (SVG 1.1 F.6.5).
  const arc = (rxIn: number, ryIn: number, rotDeg: number, large: boolean, sweep: boolean, ex: number, ey: number): void => {
    let rx = Math.abs(rxIn);
    let ry = Math.abs(ryIn);
    if (rx === 0 || ry === 0 || (ex === x && ey === y)) {
      to(ex, ey);
      return;
    }
    const phi = (rotDeg * Math.PI) / 180;
    const cos = Math.cos(phi);
    const sin = Math.sin(phi);
    const dx = (x - ex) / 2;
    const dy = (y - ey) / 2;
    const x1 = cos * dx + sin * dy;
    const y1 = -sin * dx + cos * dy;
    const lambda = (x1 * x1) / (rx * rx) + (y1 * y1) / (ry * ry);
    if (lambda > 1) {
      rx *= Math.sqrt(lambda);
      ry *= Math.sqrt(lambda);
    }
    const num2 = rx * rx * ry * ry - rx * rx * y1 * y1 - ry * ry * x1 * x1;
    const den = rx * rx * y1 * y1 + ry * ry * x1 * x1;
    let k = Math.sqrt(Math.max(0, num2 / den));
    if (large === sweep) k = -k;
    const cxp = (k * rx * y1) / ry;
    const cyp = (-k * ry * x1) / rx;
    const cx = cos * cxp - sin * cyp + (x + ex) / 2;
    const cy = sin * cxp + cos * cyp + (y + ey) / 2;
    const angle = (ux: number, uy: number, vx: number, vy: number): number => {
      const a = Math.atan2(ux * vy - uy * vx, ux * vx + uy * vy);
      return a;
    };
    const t1 = angle(1, 0, (x1 - cxp) / rx, (y1 - cyp) / ry);
    let dt = angle((x1 - cxp) / rx, (y1 - cyp) / ry, (-x1 - cxp) / rx, (-y1 - cyp) / ry);
    if (!sweep && dt > 0) dt -= 2 * Math.PI;
    if (sweep && dt < 0) dt += 2 * Math.PI;
    const steps = Math.max(2, Math.ceil(Math.abs(dt) / (Math.PI / 16)));
    for (let s = 1; s <= steps; s++) {
      const t = t1 + (dt * s) / steps;
      const px = rx * Math.cos(t);
      const py = ry * Math.sin(t);
      to(cos * px - sin * py + cx, sin * px + cos * py + cy);
    }
  };

  while (i < tokens.length) {
    if (isCommand(tokens[i])) cmd = tokens[i++]!;
    else if (!cmd || cmd === 'Z' || cmd === 'z') throw new Error('The path could not be read.');
    // Numbers after a moveto are linetos.
    else if (cmd === 'M') cmd = 'L';
    else if (cmd === 'm') cmd = 'l';
    const rel = cmd !== cmd.toUpperCase();
    const ox = rel ? x : 0;
    const oy = rel ? y : 0;
    switch (cmd.toUpperCase()) {
      case 'M': {
        flush();
        x = ox + num();
        y = oy + num();
        sx = x;
        sy = y;
        ring = [[x, y]];
        break;
      }
      case 'L':
        to(ox + num(), oy + num());
        break;
      case 'H':
        to(ox + num(), y);
        break;
      case 'V':
        to(x, oy + num());
        break;
      case 'C': {
        const x1 = ox + num(), y1 = oy + num(), x2 = ox + num(), y2 = oy + num(), ex = ox + num(), ey = oy + num();
        cubic(x1, y1, x2, y2, ex, ey);
        qx = x2;
        qy = y2;
        break;
      }
      case 'S': {
        const smooth = /[CcSs]/.test(prev);
        const x1 = smooth ? 2 * x - qx : x;
        const y1 = smooth ? 2 * y - qy : y;
        const x2 = ox + num(), y2 = oy + num(), ex = ox + num(), ey = oy + num();
        cubic(x1, y1, x2, y2, ex, ey);
        qx = x2;
        qy = y2;
        break;
      }
      case 'Q': {
        const x1 = ox + num(), y1 = oy + num(), ex = ox + num(), ey = oy + num();
        quad(x1, y1, ex, ey);
        qx = x1;
        qy = y1;
        break;
      }
      case 'T': {
        const smooth = /[QqTt]/.test(prev);
        const x1 = smooth ? 2 * x - qx : x;
        const y1 = smooth ? 2 * y - qy : y;
        const ex = ox + num(), ey = oy + num();
        quad(x1, y1, ex, ey);
        qx = x1;
        qy = y1;
        break;
      }
      case 'A': {
        const rx = num(), ry = num(), rot = num(), large = num() !== 0, sweep = num() !== 0;
        arc(rx, ry, rot, large, sweep, ox + num(), oy + num());
        break;
      }
      case 'Z':
        flush();
        x = sx;
        y = sy;
        break;
      default:
        throw new Error('The path could not be read.');
    }
    prev = cmd;
  }
  flush();
  return out;
}

// ── The writable region ──────────────────────────────────────────────────────

/** Rows a region is sampled in, over the box's height — relative, so a mockup copy breaks lines like the print. */
export const TEXT_FLOW_ROWS = 512;

/**
 * Where a box cut by a shape lets text go, row by row in BOX pixels (x from
 * the box's left edge, y from its top). Each row holds the one span that
 * contains the box's middle — the stretch of the shape a centred line can
 * use without jumping a gap — or nothing.
 */
export interface TextFlowRegion {
  width: number;
  height: number;
  rows: number;
  /** Row r's span is [left[r], right[r]]; NaN where the row has none. */
  left: Float64Array;
  right: Float64Array;
  /** The first and last usable rows' outer edges; `top >= bottom` when nothing is usable. */
  top: number;
  bottom: number;
  /** The usable area's vertical centre of mass — where "center" puts a block of text. */
  centroid: number;
  /** The widest span. */
  widest: number;
}

interface Edge {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

/** The fit shape's polygons in box pixels. Throws on an unreadable path. */
export function fitShapePolygons(shape: TextFitShape, boxW: number, boxH: number): FlowPoint[][] {
  const f = shape.frame;
  return pathPolygons(shape.d).map((ring) =>
    ring.map(([u, v]): FlowPoint => [(f.x + u * f.w) * boxW, (f.y + v * f.h) * boxH]),
  );
}

/** The margin in box pixels: `inset` of the shape's smaller side. */
export function fitShapeInsetPx(shape: TextFitShape, boxW: number, boxH: number): number {
  const inset = Number.isFinite(shape.inset) ? Math.min(0.45, Math.max(0, shape.inset!)) : 0;
  return inset * Math.min(Math.abs(shape.frame.w * boxW), Math.abs(shape.frame.h * boxH));
}

/** The x-range on scanline `y` within distance `r` of segment e, or null. */
function capsuleSpan(e: Edge, r: number, y: number): [number, number] | null {
  if (y < Math.min(e.y0, e.y1) - r || y > Math.max(e.y0, e.y1) + r) return null;
  let lo = Infinity;
  let hi = -Infinity;
  for (const [px, py] of [[e.x0, e.y0], [e.x1, e.y1]] as const) {
    const dy = y - py;
    if (Math.abs(dy) <= r) {
      const h = Math.sqrt(r * r - dy * dy);
      lo = Math.min(lo, px - h);
      hi = Math.max(hi, px + h);
    }
  }
  const dx = e.x1 - e.x0;
  const dy = e.y1 - e.y0;
  const len = Math.hypot(dx, dy);
  if (len > 0) {
    const ux = dx / len;
    const uy = dy / len;
    // Along the segment: 0 <= (p - p0)·u <= len. Across it: |(p - p0)·n| <= r.
    const along = solve(ux, (y - e.y0) * uy - e.x0 * ux, 0, len);
    const across = solve(-uy, (y - e.y0) * ux + e.x0 * uy, -r, r);
    if (along && across) {
      const a = Math.max(along[0], across[0]);
      const b = Math.min(along[1], across[1]);
      if (a <= b) {
        lo = Math.min(lo, a);
        hi = Math.max(hi, b);
      }
    }
  }
  return lo <= hi ? [lo, hi] : null;
}

/** The x with c0 <= a·x + b <= c1. */
function solve(a: number, b: number, c0: number, c1: number): [number, number] | null {
  if (Math.abs(a) < 1e-12) return b >= c0 && b <= c1 ? [-Infinity, Infinity] : null;
  const p = (c0 - b) / a;
  const q = (c1 - b) / a;
  return p < q ? [p, q] : [q, p];
}

/** The inside span of scanline `y` that holds `axis`, pulled in by `r`; null when there is none. */
function spanAround(edges: Edge[], y: number, axis: number, evenOdd: boolean, r: number): [number, number] | null {
  const crossings: Array<[number, number]> = [];
  for (const e of edges) {
    if (e.y0 === e.y1) continue;
    const down = e.y1 > e.y0;
    const ya = down ? e.y0 : e.y1;
    const yb = down ? e.y1 : e.y0;
    if (y < ya || y >= yb) continue;
    crossings.push([e.x0 + ((y - e.y0) * (e.x1 - e.x0)) / (e.y1 - e.y0), down ? 1 : -1]);
  }
  crossings.sort((a, b) => a[0] - b[0]);
  const inside = (w: number): boolean => (evenOdd ? (w & 1) === 1 : w !== 0);
  let wind = 0;
  let start = NaN;
  let span: [number, number] | null = null;
  for (const [x, w] of crossings) {
    const was = inside(wind);
    wind += w;
    const now = inside(wind);
    if (!was && now) start = x;
    else if (was && !now) {
      if (start <= axis && axis <= x) {
        span = [start, x];
        break;
      }
    }
  }
  if (!span) return null;
  let [left, right] = span;
  if (r > 0) {
    for (const e of edges) {
      const c = capsuleSpan(e, r, y);
      if (!c) continue;
      if (c[0] <= axis && axis <= c[1]) return null;
      if (c[1] < axis) {
        if (c[1] > left) left = c[1];
      } else if (c[0] < right) right = c[0];
    }
  }
  return left < right ? [left, right] : null;
}

const regionCache = new Map<string, TextFlowRegion | null>();
const REGION_CACHE_LIMIT = 64;

/**
 * The region a box of `boxW` × `boxH` cut by `shape` leaves for text. Null
 * when the outline cannot be read (the text then flows in its plain box,
 * and `textFitShapeProblems` reports it).
 */
export function textFlowRegion(shape: TextFitShape, boxW: number, boxH: number): TextFlowRegion | null {
  const key = JSON.stringify([shape.d, shape.frame, shape.inset, shape.fillRule, boxW, boxH]);
  if (regionCache.has(key)) return regionCache.get(key)!;
  let region: TextFlowRegion | null;
  try {
    region = measureRegion(shape, boxW, boxH);
  } catch {
    region = null;
  }
  regionCache.set(key, region);
  if (regionCache.size > REGION_CACHE_LIMIT) regionCache.delete(regionCache.keys().next().value!);
  return region;
}

function measureRegion(shape: TextFitShape, boxW: number, boxH: number): TextFlowRegion | null {
  if (!(boxW > 0) || !(boxH > 0)) return null;
  const polygons = fitShapePolygons(shape, boxW, boxH);
  if (polygons.length === 0) return null;
  const edges: Edge[] = [];
  for (const ring of polygons) {
    for (let k = 0; k < ring.length; k++) {
      const [x0, y0] = ring[k]!;
      const [x1, y1] = ring[(k + 1) % ring.length]!;
      if (x0 === x1 && y0 === y1) continue;
      edges.push({ x0, y0, x1, y1 });
    }
  }
  const r = fitShapeInsetPx(shape, boxW, boxH);
  const evenOdd = shape.fillRule === 'evenodd';
  const axis = boxW / 2;
  const rows = TEXT_FLOW_ROWS;
  const rowH = boxH / rows;
  const at = (y: number): [number, number] | null => spanAround(edges, y, axis, evenOdd, r);
  const left = new Float64Array(rows).fill(NaN);
  const right = new Float64Array(rows).fill(NaN);
  let top = Infinity;
  let bottom = -Infinity;
  let mass = 0;
  let moment = 0;
  let widest = 0;
  let above = at(0);
  for (let row = 0; row < rows; row++) {
    const mid = at((row + 0.5) * rowH);
    const below = at((row + 1) * rowH);
    if (above && mid && below) {
      const l = Math.max(0, above[0], mid[0], below[0]);
      const rr = Math.min(boxW, above[1], mid[1], below[1]);
      if (l < rr) {
        left[row] = l;
        right[row] = rr;
        top = Math.min(top, row * rowH);
        bottom = Math.max(bottom, (row + 1) * rowH);
        mass += rr - l;
        moment += (rr - l) * (row + 0.5) * rowH;
        widest = Math.max(widest, rr - l);
      }
    }
    above = below;
  }
  return {
    width: boxW,
    height: boxH,
    rows,
    left,
    right,
    top: Number.isFinite(top) ? top : 0,
    bottom: Number.isFinite(bottom) ? bottom : 0,
    centroid: mass > 0 ? moment / mass : boxH / 2,
    widest,
  };
}

/** The span every row from y0 to y1 shares, or null when one of them has none. */
export function regionSpan(region: TextFlowRegion, y0: number, y1: number): [number, number] | null {
  const eps = region.height * 1e-9;
  if (y0 < -eps || y1 > region.height + eps || y1 < y0) return null;
  const rowH = region.height / region.rows;
  const r0 = Math.max(0, Math.floor(y0 / rowH));
  const r1 = Math.min(region.rows - 1, Math.max(r0, Math.ceil(y1 / rowH) - 1));
  let l = -Infinity;
  let r = Infinity;
  for (let row = r0; row <= r1; row++) {
    const a = region.left[row]!;
    if (Number.isNaN(a)) return null;
    if (a > l) l = a;
    const b = region.right[row]!;
    if (b < r) r = b;
  }
  return l < r ? [l, r] : null;
}

/**
 * The usable region's outline in box pixels, for an editor to draw: the
 * left edges top to bottom, then the right edges back up. Empty when no row
 * is usable. Runs of unusable rows split it into several outlines.
 */
export function regionOutlines(region: TextFlowRegion): FlowPoint[][] {
  const rowH = region.height / region.rows;
  const out: FlowPoint[][] = [];
  let run: number[] = [];
  const close = (): void => {
    if (run.length === 0) return;
    const lefts: FlowPoint[] = [];
    const rights: FlowPoint[] = [];
    for (const row of run) {
      lefts.push([region.left[row]!, row * rowH], [region.left[row]!, (row + 1) * rowH]);
      rights.push([region.right[row]!, row * rowH], [region.right[row]!, (row + 1) * rowH]);
    }
    out.push([...lefts, ...rights.reverse()]);
    run = [];
  };
  for (let row = 0; row < region.rows; row++) {
    if (Number.isNaN(region.left[row]!)) close();
    else run.push(row);
  }
  close();
  return out;
}

// ── Fitting ──────────────────────────────────────────────────────────────────

/** The measurements a renderer supplies, all at font size 1. */
export interface TextFlowFont {
  /** Advance width of `text`. */
  advance(text: string): number;
  /** The line box above and below the baseline, and baseline to baseline — line spacing included. */
  lineAscent: number;
  lineDescent: number;
  lineStep: number;
  /** The font's own ascent and descent: the band a line's letters occupy in a shape. */
  inkAscent: number;
  inkDescent: number;
}

export interface TextFlowInput {
  text: string;
  /** The box, in scene pixels. */
  width: number;
  height: number;
  /** The box cut by a shape; absent = the whole box. */
  region?: TextFlowRegion | null;
  minSize: number;
  maxSize: number;
  /** Lines may break between words. Without it each hard line stays one line. */
  wrap: boolean;
  maxLines?: number;
  align: 'left' | 'center' | 'right' | 'justify';
  verticalAlign: 'top' | 'center' | 'bottom';
  font: TextFlowFont;
}

export interface TextFlowLine {
  text: string;
  /** Where the line's first character is in the input text (alternating colours continue from it). */
  start: number;
  /** Advance width at the flowed size, as measured word by word. */
  width: number;
  /** The span the line may use, in box pixels from the box's left edge. */
  left: number;
  right: number;
  /** Where the line starts once aligned. */
  x: number;
  /** The baseline, in box pixels from the box's top edge. */
  baseline: number;
  /** Extra pixels at every word gap of a justified line. */
  wordSpacing?: number;
  /** The last line of its paragraph — justification leaves it ragged. */
  paragraphEnd: boolean;
}

export interface TextFlowResult {
  size: number;
  lines: TextFlowLine[];
  /** Not even the smallest size fits: lines run past the box or the shape. */
  overflow: boolean;
  /**
   * Text in a shape that did not fit at its smallest size and was made
   * smaller still rather than printed over the shape's edge. Still a text
   * too long for its layer: Studio and render reviews report it.
   */
  belowMinSize?: boolean;
}

/** The font-size range a text auto-fits in — the same reading as the renderer's fit. */
export function textFitRange(el: TextElement): { min: number; max: number } {
  if (el.font.size && !el.font.minSize && !el.font.maxSize) return { min: el.font.size, max: el.font.size };
  const max = el.font.maxSize ?? el.font.size ?? 100;
  const min = el.font.minSize && el.font.minSize > 0 ? Math.min(el.font.minSize, max) : Math.min(1, max);
  return { min, max };
}

/**
 * Whether a straight text is laid out by `flowText` rather than one wrapped
 * paragraph: anything with a fit shape, and a multiline text that
 * auto-fits. A fixed-size multiline text (the designer's fixed box) keeps
 * plain word wrap at its box width, and vertical text keeps its one letter
 * per line.
 */
export function usesTextFlow(el: TextElement): boolean {
  if (el.curve || el.warp) return false;
  if (el.fitShape) return true;
  if (!el.multiline || el.vertical) return false;
  const { min, max } = textFitRange(el);
  return max > min;
}

/** A word, or a blank line; widths at size 1. */
interface Item {
  text: string;
  start: number;
  w: number;
  /** The blank run before the word, dropped when a line breaks there. */
  gap: string;
  g: number;
  para: number;
  blank: boolean;
}

// Whitespace a line may break at. No-break spaces (U+00A0, U+2007, U+202F) hold words together.
const BREAKS = /[\t\v\f\r   -  -  　]+/gu;
// How far a line may run past its span: float noise, relative so every scale agrees.
const SLACK = 1e-4;

function tokenize(text: string, font: TextFlowFont): Item[] {
  const items: Item[] = [];
  const widths = new Map<string, number>();
  const advance = (s: string): number => {
    let w = widths.get(s);
    if (w === undefined) {
      w = font.advance(s);
      widths.set(s, w);
    }
    return w;
  };
  let offset = 0;
  text.split('\n').forEach((para, p) => {
    const start = offset;
    offset += para.length + 1;
    const words: Array<{ text: string; at: number; gap: string }> = [];
    let last = 0;
    let gap = '';
    for (const m of para.matchAll(BREAKS)) {
      if (m.index! > last) words.push({ text: para.slice(last, m.index), at: last, gap });
      gap = m[0];
      last = m.index! + m[0].length;
    }
    if (last < para.length) words.push({ text: para.slice(last), at: last, gap });
    if (words.length === 0) {
      items.push({ text: '', start, w: 0, gap: '', g: 0, para: p, blank: true });
      return;
    }
    words.forEach((word, k) => {
      if (k === 0) {
        // Leading blanks stay with the first word: an indent the shopper typed.
        const t = word.gap + word.text;
        items.push({ text: t, start: start + word.at - word.gap.length, w: advance(t), gap: '', g: 0, para: p, blank: false });
      } else {
        items.push({ text: word.text, start: start + word.at, w: advance(word.text), gap: word.gap, g: advance(word.gap), para: p, blank: false });
      }
    });
  });
  return items;
}

interface Plan {
  /** Inclusive item ranges, one per line. */
  ranges: Array<[number, number]>;
  baselines: number[];
  spans: Array<[number, number]>;
}

/**
 * Lays `input.text` out at the largest size that fits, preferring fewer
 * lines at that size, and balances the lines it settles on.
 */
export function flowText(input: TextFlowInput): TextFlowResult {
  const { font } = input;
  const items = tokenize(input.text, font);
  const region = input.region && input.region.bottom > input.region.top ? input.region : null;
  const centred = input.align === 'center';

  // Prefix sums for a line's width: items a..b is S[b+1] - S[a] - g[a].
  const S = new Float64Array(items.length + 1);
  for (let k = 0; k < items.length; k++) S[k + 1] = S[k]! + items[k]!.g + items[k]!.w;
  const lineW = (a: number, b: number): number => S[b + 1]! - S[a]! - items[a]!.g;
  const paragraphs = items.length ? items[items.length - 1]!.para + 1 : 0;
  const wordSum = items.reduce((sum, it) => sum + it.w, 0);
  const maxLines = Math.max(
    paragraphs,
    Math.min(items.length, input.maxLines && input.maxLines > 0 ? Math.floor(input.maxLines) : Infinity),
  );

  /** Greedy fill: the fewest lines, each as full as its width allows. */
  const greedy = (widths: number[], s: number): Array<[number, number]> | null => {
    const ranges: Array<[number, number]> = [];
    let a = 0;
    while (a < items.length) {
      const li = ranges.length;
      if (li >= widths.length) return null;
      const it = items[a]!;
      if (it.blank) {
        ranges.push([a, a]);
        a++;
        continue;
      }
      const room = widths[li]! * (1 + SLACK);
      let b = a;
      while (b + 1 < items.length && items[b + 1]!.para === it.para && (!input.wrap || lineW(a, b + 1) * s <= room)) b++;
      if (!(lineW(a, b) * s <= room)) return null;
      ranges.push([a, b]);
      a = b + 1;
    }
    return ranges;
  };

  /**
   * Exactly `n` lines with the least raggedness — relative slack squared,
   * so a heart's wide lines and narrow lines are judged alike. A centred
   * text balances every line; otherwise a paragraph's last line may be
   * short, as in any book.
   */
  const balance = (widths: number[], s: number, n: number): Array<[number, number]> | null => {
    const m = items.length;
    if (m > 160) return null;
    const cost: Float64Array[] = [];
    const from: Int32Array[] = [];
    for (let i = 0; i <= n; i++) {
      cost.push(new Float64Array(m + 1).fill(Infinity));
      from.push(new Int32Array(m + 1).fill(-1));
    }
    cost[0]![0] = 0;
    for (let i = 0; i < n; i++) {
      const W = widths[i]!;
      const room = W * (1 + SLACK);
      for (let j = 0; j < m; j++) {
        const base = cost[i]![j]!;
        if (base === Infinity) continue;
        if (items[j]!.blank) {
          if (base < cost[i + 1]![j + 1]!) {
            cost[i + 1]![j + 1] = base;
            from[i + 1]![j + 1] = j;
          }
          continue;
        }
        const para = items[j]!.para;
        for (let k = j + 1; k <= m; k++) {
          const lastItem = items[k - 1]!;
          if (lastItem.blank || lastItem.para !== para) break;
          const lw = lineW(j, k - 1) * s;
          if (lw > room) break;
          const end = k === m || items[k]!.para !== para;
          if (!input.wrap && !end) continue;
          const slack = W > 0 ? Math.max(0, (W - lw) / W) : 0;
          const c = base + (end && !centred ? 0.2 : 1) * slack * slack;
          if (c < cost[i + 1]![k]!) {
            cost[i + 1]![k] = c;
            from[i + 1]![k] = j;
          }
        }
      }
    }
    if (cost[n]![m] === Infinity) return null;
    const ranges: Array<[number, number]> = [];
    let k = m;
    for (let i = n; i > 0; i--) {
      const j = from[i]![k]!;
      ranges.unshift([j, k - 1]);
      k = j;
    }
    return ranges;
  };

  const settle = (widths: number[], s: number, n: number): Array<[number, number]> | null => {
    const g = greedy(widths.slice(0, n), s);
    if (!g) return null;
    if (g.length === n) return balance(widths, s, n) ?? g;
    return balance(widths, s, n);
  };

  /** A plan in the plain box. */
  const planBox = (s: number): Plan | null => {
    const widths = new Array<number>(maxLines).fill(input.width);
    const g = greedy(widths, s);
    if (!g) return null;
    const n = g.length;
    const block = (font.lineAscent + font.lineDescent + (n - 1) * font.lineStep) * s;
    if (block > input.height * (1 + SLACK)) return null;
    const ranges = balance(widths, s, n) ?? g;
    const top =
      input.verticalAlign === 'top' ? 0 : input.verticalAlign === 'bottom' ? input.height - block : (input.height - block) / 2;
    const baselines = ranges.map((_, i) => top + (font.lineAscent + i * font.lineStep) * s);
    return { ranges, baselines, spans: ranges.map(() => [0, input.width]) };
  };

  /** A plan inside the shape: the fewest lines, as near the alignment's place as fits. */
  const planShape = (s: number, r: TextFlowRegion): Plan | null => {
    const inkA = font.inkAscent * s;
    const inkD = font.inkDescent * s;
    const step = font.lineStep * s;
    const usable = r.bottom - r.top;
    const rowH = r.height / r.rows;
    // Each line holds at most the widest span, so fewer lines than this cannot hold the words.
    const fewest = Math.max(paragraphs, r.widest > 0 ? Math.ceil((wordSum * s) / r.widest - 1e-9) : paragraphs);
    // Without wrapping every typed line is exactly one line.
    const most = input.wrap ? maxLines : paragraphs;
    for (let n = input.wrap ? Math.max(1, fewest) : paragraphs; n <= most; n++) {
      if (inkA + inkD + (n - 1) * step > usable * (1 + SLACK)) break;
      const lo = r.top + inkA;
      const hi = r.bottom - inkD - (n - 1) * step;
      const inkMiddle = ((n - 1) * step + inkD - inkA) / 2;
      const target =
        input.verticalAlign === 'top' ? lo : input.verticalAlign === 'bottom' ? hi : Math.min(hi, Math.max(lo, r.centroid - inkMiddle));
      const delta = Math.max((hi - lo) / 24, rowH);
      const candidates = [target];
      for (let k = 1; k <= 48; k++) {
        const up = target - k * delta;
        const down = target + k * delta;
        if (up < lo - 1e-9 && down > hi + 1e-9) break;
        if (down <= hi + 1e-9) candidates.push(down);
        if (up >= lo - 1e-9) candidates.push(up);
      }
      for (const b0 of candidates) {
        const spans: Array<[number, number]> = [];
        const widths: number[] = [];
        for (let i = 0; i < n; i++) {
          const b = b0 + i * step;
          const span = regionSpan(r, b - inkA, b + inkD) ?? [r.width / 2, r.width / 2];
          spans.push(span);
          widths.push(span[1] - span[0]);
        }
        const ranges = settle(widths, s, n);
        if (!ranges) continue;
        return { ranges, baselines: spans.map((_, i) => b0 + i * step), spans };
      }
    }
    return null;
  };

  const plan = (s: number): Plan | null => (region ? planShape(s, region) : planBox(s));

  const max = Math.max(input.maxSize, 0.01);
  const min = Math.min(Math.max(input.minSize, 0.01), max);
  let size = max;
  let best = plan(max);
  if (!best) {
    let lo = min;
    let hi = max;
    let found: Plan | null = null;
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      const p = plan(mid);
      if (p) {
        lo = mid;
        found = p;
      } else hi = mid;
    }
    size = lo;
    best = found ?? plan(min);
    if (!found) size = min;
  }
  if (!best && region) {
    // A shape is the edge of the print (a heart on a box lid): rather than
    // spill over it, the text goes on shrinking â€” to a third of its smallest
    // size at most â€” and says so.
    let lo = min / 3;
    let hi = min;
    let found: Plan | null = null;
    for (let i = 0; i < 12; i++) {
      const mid = (lo + hi) / 2;
      const p = plan(mid);
      if (p) {
        lo = mid;
        found = p;
      } else hi = mid;
    }
    if (found) return { ...place(input, items, found, lo, lineW), belowMinSize: true };
  }
  if (!best) return overflowLayout(input, items, min, region);
  return place(input, items, best, size, lineW);
}

/** Lines from a plan, aligned in their spans. */
function place(
  input: TextFlowInput,
  items: Item[],
  plan: Plan,
  size: number,
  lineW: (a: number, b: number) => number,
): TextFlowResult {
  const lines: TextFlowLine[] = plan.ranges.map(([a, b], i) => {
    const [left, right] = plan.spans[i]!;
    const blank = items[a]!.blank;
    const width = blank ? 0 : lineW(a, b) * size;
    const paragraphEnd = b + 1 >= items.length || items[b + 1]!.para !== items[a]!.para;
    let text = items[a]!.text;
    for (let k = a + 1; k <= b; k++) text += items[k]!.gap + items[k]!.text;
    let x =
      input.align === 'center' ? (left + right) / 2 - width / 2 : input.align === 'right' ? right - width : left;
    let wordSpacing: number | undefined;
    if (input.align === 'justify' && !paragraphEnd && b > a) {
      wordSpacing = Math.max(0, (right - left - width) / (b - a));
      x = left;
    }
    return { text, start: items[a]!.start, width, left, right, x, baseline: plan.baselines[i]!, paragraphEnd, ...(wordSpacing ? { wordSpacing } : {}) };
  });
  return { size, lines, overflow: false };
}

const segmenter =
  typeof Intl !== 'undefined' && typeof Intl.Segmenter === 'function'
    ? new Intl.Segmenter(undefined, { granularity: 'grapheme' })
    : undefined;
const graphemesOf = (s: string): string[] => (segmenter ? Array.from(segmenter.segment(s), (g) => g.segment) : [...s]);

/**
 * Nothing fits even at the smallest size: the text is laid out at it, in the
 * widest span, words broken where they run out of room, and reported as
 * overflowing — the readiness check and the shop's warning read that.
 */
function overflowLayout(input: TextFlowInput, items: Item[], size: number, region: TextFlowRegion | null): TextFlowResult {
  const { font } = input;
  let left = 0;
  let right = input.width;
  if (region && region.widest > 0) {
    for (let row = 0; row < region.rows; row++) {
      const l = region.left[row]!;
      if (!Number.isNaN(l) && region.right[row]! - l >= region.widest) {
        left = l;
        right = region.right[row]!;
        break;
      }
    }
  }
  const room = (right - left) * (1 + SLACK);
  type Piece = { text: string; start: number; w: number; paragraphEnd: boolean };
  const pieces: Piece[] = [];
  let cur: Omit<Piece, 'paragraphEnd'> | null = null;
  let curPara = -1;
  const end = (paragraphEnd: boolean): void => {
    if (cur) pieces.push({ ...cur, paragraphEnd });
    cur = null;
  };
  for (const it of items) {
    if (it.para !== curPara) end(true);
    curPara = it.para;
    if (it.blank) {
      pieces.push({ text: '', start: it.start, w: 0, paragraphEnd: true });
      continue;
    }
    const open = cur as Omit<Piece, 'paragraphEnd'> | null;
    if (open) {
      const joined = open.w + (it.g + it.w) * size;
      if (!input.wrap || joined <= room) {
        cur = { text: open.text + it.gap + it.text, start: open.start, w: joined };
        continue;
      }
      end(false);
    }
    if (!input.wrap || it.w * size <= room) {
      cur = { text: it.text, start: it.start, w: it.w * size };
      continue;
    }
    // A word wider than the line, broken where it runs out of room.
    let chunk = '';
    let chunkStart = it.start;
    let offset = it.start;
    for (const g of graphemesOf(it.text)) {
      if (chunk && font.advance(chunk + g) * size > room) {
        pieces.push({ text: chunk, start: chunkStart, w: font.advance(chunk) * size, paragraphEnd: false });
        chunk = '';
        chunkStart = offset;
      }
      chunk += g;
      offset += g.length;
    }
    cur = { text: chunk, start: chunkStart, w: font.advance(chunk) * size };
  }
  end(true);
  const n = pieces.length;
  const block = (font.lineAscent + font.lineDescent + (n - 1) * font.lineStep) * size;
  const top =
    input.verticalAlign === 'top' ? 0 : input.verticalAlign === 'bottom' ? input.height - block : (input.height - block) / 2;
  const lines: TextFlowLine[] = pieces.map((p, i) => {
    const x = input.align === 'center' ? (left + right) / 2 - p.w / 2 : input.align === 'right' ? right - p.w : left;
    return { text: p.text, start: p.start, width: p.w, left, right, x, baseline: top + (font.lineAscent + i * font.lineStep) * size, paragraphEnd: p.paragraphEnd };
  });
  return { size, lines, overflow: true };
}
